import { merge } from 'lodash';
import { CartesianGrid, LabelList, Line, LineChart, XAxis, YAxis } from 'recharts';

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart/base';
import { ChartLineProps } from '@/types/charts';
import { cn } from '@/utils/styles';

import { ChartLastDot } from '../last-dot';

const ChartLine = ({ data, chartConfig, settings, showLabelList, showMarkerEnd = true, className }: ChartLineProps) => {
  const seriesKeys = Object.keys(chartConfig);
  const { chart, grid, axes, labelList, legend } = settings;
  const { xAxis, yAxis } = axes ?? {};
  const { valuePrefix, valueSuffix, tooltipSubHeading, hideTooltipLabel, labelFormatter } = settings.tooltip ?? {};

  const seriesPropsWithColor = seriesKeys.reduce<Record<string, any>>(
    (acc, key) => ({
      ...acc,
      [key]: merge({}, settings.series, {
        dataKey: key,
        stroke: `var(--color-${key})`,
        fill: `var(--color-${key})`,
        activeDot: {
          r: 4,
          stroke: 'white',
          strokeWidth: 2,
          fill: `var(--color-${key})`,
        },
      }),
    }),
    {},
  );

  return (
    <ChartContainer config={chartConfig} className={cn('min-h-[200px] w-full', className)}>
      <LineChart accessibilityLayer data={data} {...chart}>
        <CartesianGrid {...grid} />
        <XAxis {...xAxis} />
        <YAxis {...yAxis} />

        {!settings.tooltip?.hide && (
          <ChartTooltip
            cursor={settings.tooltip?.cursor}
            content={
              <ChartTooltipContent
                valuePrefix={valuePrefix}
                valueSuffix={valueSuffix}
                tooltipSubHeading={tooltipSubHeading}
                hideLabel={hideTooltipLabel}
                labelFormatter={labelFormatter}
              />
            }
          />
        )}

        {seriesKeys.map((key) => {
          return (
            <Line
              key={key}
              {...seriesPropsWithColor[key]}
              dot={
                showMarkerEnd
                  ? // eslint-disable-next-line react/no-unstable-nested-components
                    (props) => (
                      <ChartLastDot props={props} data={data} dataKey={key} key={`${key}-last-dot-${props.index}`} />
                    )
                  : false
              }
            >
              {showLabelList && <LabelList dataKey={key} {...labelList} />}
            </Line>
          );
        })}

        {!legend?.hide && <ChartLegend content={<ChartLegendContent textSize={legend?.textSize} />} />}
      </LineChart>
    </ChartContainer>
  );
};

export { ChartLine };
